// MAC cycle-window math, frontend copy.
//
// Mirrored in supabase/functions/_shared/quota/mac.ts for the edge functions.
// The two copies must stay logically identical: both are unit-tested in
// src/lib/macQuota.test.ts so drift is caught at CI time.
//
// Frontend can't import the edge-function file directly because supabase/ is
// outside the Docker build context. Backend can't import src/ because edge
// functions ship via Deno with a different module resolver.

export function addMonthsUTC(base: Date, n: number): Date {
  const y = base.getUTCFullYear();
  const m = base.getUTCMonth();
  const d = base.getUTCDate();
  const targetMonthAbs = m + n;
  const targetY = y + Math.floor(targetMonthAbs / 12);
  const targetM = ((targetMonthAbs % 12) + 12) % 12;
  // Last day of target month. Used to clamp day overflow.
  const lastDayOfTargetMonth = new Date(Date.UTC(targetY, targetM + 1, 0)).getUTCDate();
  const clampedD = Math.min(d, lastDayOfTargetMonth);
  return new Date(Date.UTC(
    targetY, targetM, clampedD,
    base.getUTCHours(), base.getUTCMinutes(), base.getUTCSeconds(), base.getUTCMilliseconds(),
  ));
}

export function getCurrentCycleWindow(anchor: Date, now: Date): { start: Date; end: Date } {
  if (now < anchor) {
    // Cycle hasn't started yet. Return the first cycle.
    return { start: anchor, end: addMonthsUTC(anchor, 1) };
  }
  const msPerMonthApprox = 30.44 * 24 * 3600 * 1000;
  let n = Math.max(0, Math.floor((now.getTime() - anchor.getTime()) / msPerMonthApprox));
  // Adjust forward/backward. Month length variation means estimate can be ±1.
  while (addMonthsUTC(anchor, n + 1).getTime() <= now.getTime()) n++;
  while (n > 0 && addMonthsUTC(anchor, n).getTime() > now.getTime()) n--;
  return { start: addMonthsUTC(anchor, n), end: addMonthsUTC(anchor, n + 1) };
}

export function toCycleStartDateString(d: Date): string {
  // Returns YYYY-MM-DD in UTC. Matches the DATE column type in monthly_mac_usage.
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Cycle_start (YYYY-MM-DD, UTC) of the cycle currently in progress for `anchor`.
export function currentCycleStartString(anchor: Date, now: Date = new Date()): string {
  return toCycleStartDateString(getCurrentCycleWindow(anchor, now).start);
}

// True if `cycleStartStr` (YYYY-MM-DD) refers to a cycle that has already CLOSED
// i.e. it is strictly before the cycle currently in progress. Used by the
// broadcast-overage billing cron: only closed cycles are safe to invoice (an
// open cycle is still accumulating charges).
export function isCycleClosed(
  anchor: Date,
  cycleStartStr: string,
  now: Date = new Date(),
): boolean {
  return cycleStartStr < currentCycleStartString(anchor, now);
}
